import React, { useState } from 'react';
import { Layout, Typography, Button, Modal, Space, Menu, Alert } from 'antd';
import { 
  LogoutOutlined, 
  UserOutlined, 
  MenuOutlined,
  DashboardOutlined,
  TeamOutlined,
  BankOutlined,
  SettingOutlined,
  FileTextOutlined,
  ApiOutlined,
  HistoryOutlined,
  BarChartOutlined,
  SwapOutlined,
  CrownOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { useAuthStore } from '../store/authStore';
import { useNavigate, useLocation } from 'react-router-dom';
import { clientAPI } from '../services/api';
import { message } from 'antd';

const { Header } = Layout;
const { Title, Text } = Typography;

const AppHeader: React.FC = () => {
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);
  const { 
    user, 
    selectedClient, 
    logout, 
    originalUser, 
    isImpersonating, 
    clearImpersonation 
  } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Debug logging
  console.log('Header Debug:', {
    user: user,
    originalUser: originalUser,
    isImpersonating: isImpersonating,
    selectedClient: selectedClient,
    userRole: user?.role,
    originalUserRole: originalUser?.role,
    userPermissions: user?.permissions,
    shouldShowSwitchButton: (originalUser?.role === 'super_admin' || (user?.role === 'super_admin' && !isImpersonating))
  });

  const handleSwitchClient = () => {
    // Simple approach: navigate to switch user page
    navigate('/switch-user');
  };

  const handleLogout = () => {
    logout();
    setLogoutModalVisible(false);
    navigate('/login');
  };

  const showLogoutModal = () => {
    setLogoutModalVisible(true);
  };

  // Helper function to check if user has permission (supporting both exact and legacy permissions)
  const hasPermission = (requiredPermission: string): boolean => {
    if (!user?.permissions) return false;
    
    // Check for exact permission match
    if (user.permissions.includes(requiredPermission)) {
      return true;
    }
    
    // Check for legacy permission (e.g., 'analytics' for 'analytics:read')
    const legacyPermission = requiredPermission.split(':')[0];
    if (user.permissions.includes(legacyPermission)) {
      return true;
    }
    
    // Check for admin-level permissions
    if (user.permissions.includes('admin') || user.role === 'super_admin') {
      return true;
    }
    
    return false;
  };

  // Navigation menu items based on user permissions
  const getNavigationItems = () => {
    const items: any[] = [
      {
        key: 'dashboard',
        icon: <DashboardOutlined />,
        label: 'Dashboard',
        onClick: () => navigate('/dashboard'),
      },
    ];

    // User Management - show if user can view users OR manage users OR is admin/super_admin
    if (hasPermission('user_management:read') || hasPermission('user_management:write') || user?.role === 'admin' || user?.role === 'super_admin') {
      items.push({
        key: 'users',
        icon: <TeamOutlined />,
        label: 'User Management',
        onClick: () => navigate('/users'),
      });
    }
    
    // Role Management - show if user can manage users OR is admin/super_admin (only user managers need this)
    if (hasPermission('user_management:write') || user?.role === 'admin' || user?.role === 'super_admin') {
      items.push({
        key: 'role-management',
        icon: <SettingOutlined />,
        label: 'Role Management',
        onClick: () => navigate('/role-management'),
      });
    }
    
    // Document Types - show if user can manage settings OR is admin/super_admin
    if (hasPermission('settings:write') || user?.role === 'admin' || user?.role === 'super_admin') {
      items.push({
        key: 'document-types',
        icon: <FileTextOutlined />,
        label: 'Document Types',
        onClick: () => navigate('/document-types'),
      });
    }

    // API Management - show if user can view API management OR is admin/super_admin
    if (hasPermission('api_management:read') || hasPermission('api_management:write') || user?.role === 'admin' || user?.role === 'super_admin') {
      items.push({
        key: 'api-management',
        icon: <ApiOutlined />,
        label: 'API Management',
        onClick: () => navigate('/api-management'),
      });
    }

    // Logs - show if user can view logs OR is admin/super_admin
    if (hasPermission('logs:read') || user?.role === 'admin' || user?.role === 'super_admin') {
      items.push({
        key: 'logs',
        icon: <HistoryOutlined />,
        label: 'Logs',
        onClick: () => navigate('/logs'),
      });
    }

    // Analytics - show if user can view analytics OR is admin/super_admin
    if (hasPermission('analytics:read') || user?.role === 'admin' || user?.role === 'super_admin') {
      items.push({
        key: 'analytics',
        icon: <BarChartOutlined />,
        label: 'Analytics',
        onClick: () => navigate('/analytics'),
      });
    }

    // Billing - show to all authenticated users
    items.push({
      key: 'billing',
      icon: <DollarOutlined />,
      label: 'Billing & Credits',
      onClick: () => navigate('/billing'),
    });

    // Client Management - show if user can view/manage clients OR is super_admin (but typically only super_admin)
    if (hasPermission('client_management:read') || hasPermission('client_management:write') || user?.role === 'super_admin') {
      items.push({
        key: 'clients',
        icon: <BankOutlined />,
        label: 'Client Management',
        onClick: () => navigate('/clients'),
      });
    }

    return items;
  };

  const navigationMenu = (
    <Menu
      items={getNavigationItems()}
      selectedKeys={[location.pathname.substring(1) || 'dashboard']}
      style={{ minWidth: 200 }}
    />
  );

  const BrandIcon = () => (
    <img 
      src="/logo.png" 
      alt="Logo" 
      style={{ 
        width: '120px', 
        height: '120px',
        objectFit: 'contain',
        verticalAlign: 'middle'
      }} 
    />
  );

  return (
    <>
      {/* Super Admin Impersonation Banner */}
      {isImpersonating && originalUser && selectedClient && (
        <Alert
          message={
            <Space>
              <CrownOutlined style={{ color: '#722ed1' }} />
              <Text strong style={{ color: '#722ed1' }}>
                Super Admin Mode: 
              </Text>
              <Text>
                Managing {selectedClient.name} as {user?.name}
              </Text>
              <Button
                size="small"
                type="link"
                icon={<SwapOutlined />}
                onClick={handleSwitchClient}
                style={{ color: '#722ed1', padding: 0 }}
              >
                Switch Client
              </Button>
            </Space>
          }
          type="info"
          style={{
            background: '#f9f0ff',
            border: '1px solid #d3adf7',
            margin: 0,
            borderRadius: 0
          }}
          showIcon={false}
        />
      )}
      
      <Header className="dashboard-header" style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        padding: '0 2rem',
        background: 'white',
        borderBottom: '1px solid #e6e9f4'
      }}>
        <Space align="center">
          <BrandIcon />
          {selectedClient && (
            <Text type="secondary" style={{ marginLeft: '1rem' }}>
              {selectedClient.name}
            </Text>
          )}
        </Space>

        <Space>
          {/* Navigation Menu */}
          <Menu
            mode="horizontal"
            items={getNavigationItems()}
            selectedKeys={[location.pathname.substring(1) || 'dashboard']}
            style={{ 
              border: 'none', 
              background: 'transparent',
              lineHeight: '64px'
            }}
          />
        </Space>
        
        <Space size="middle">
          <Space align="center" size="small" style={{ whiteSpace: 'nowrap' }}>
            <UserOutlined style={{ color: '#47569e' }} />
            <Text style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.name || user?.email}
            </Text>
            {user?.role && (
              <Text type="secondary" style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                ({user.role.replace('_', ' ').toUpperCase()})
              </Text>
            )}
          </Space>
          
          {/* Switch Client Button for Super Admins */}
          {(originalUser?.role === 'super_admin' || (user?.role === 'super_admin' && !isImpersonating)) && (
            <Button
              type="text"
              icon={<SwapOutlined />}
              onClick={handleSwitchClient}
              style={{ color: '#47569e' }}
              title={isImpersonating ? "Switch to different client" : "Select client to manage"}
            >
              {isImpersonating ? "Switch Client" : "Select Client"}
            </Button>
          )}
          
          <Button 
            type="text" 
            icon={<LogoutOutlined />}
            onClick={showLogoutModal}
            style={{ color: '#47569e' }}
          >
            Logout
          </Button>
        </Space>
      </Header>

      <Modal
        title="Confirm Logout"
        open={logoutModalVisible}
        onOk={handleLogout}
        onCancel={() => setLogoutModalVisible(false)}
        okText="Confirm"
        cancelText="Cancel"
        okButtonProps={{ 
          style: { 
            background: '#607afb',
            borderColor: '#607afb' 
          } 
        }}
      >
        <p>Are you sure you want to logout?</p>
      </Modal>
    </>
  );
};

export default AppHeader;
